import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { BaixarBoletoViaTokenController } from "../../../../Controllers/Cobranca/Boleto/BoletoController";
import { IBoletoViaTokenDTO } from "../../../../Interfaces/Cobranca/Boleto/BoletoInterface";
import { AlertError, AlertSuccess } from "../../../../Tools/SweetAlerts";
import { FaCopy, FaDownload } from "react-icons/fa";
import { Row, Col, Button, Spinner, Card, Modal, Form } from "react-bootstrap";

export default function ExibicaoBoleto() {
    const { token } = useParams<{ token: string }>();

    const [pdfBase64, setPdfBase64] = useState<string>("");
    const [imgBase64List, setImgBase64List] = useState<string[]>([]);
    const [linhaDigitavelList, setLinhaDigitavelList] = useState<string[]>([]);
    const [selectedLinhaDigitavel, setSelectedLinhaDigitavel] =
        useState<string>("");
    const [nomeCliente, setNomeCliente] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedImg, setSelectedImg] = useState<string>("");
    const [showCpfModal, setShowCpfModal] = useState<boolean>(true);
    const [cpf, setCpf] = useState<string>("");
    const [cpfError, setCpfError] = useState<string>("");

    const fetchBoletoData = async (cpfNumeros: string) => {
        if (token && cpfNumeros) {
            try {
                console.log(
                    `Buscando dados do boleto com token: ${token} e CPF/CNPJ: ${cpfNumeros}`
                );
                setLoading(true);
                const boleto: IBoletoViaTokenDTO =
                    await BaixarBoletoViaTokenController(token, cpfNumeros);
                setPdfBase64(boleto.PDF_BOLETO);
                setImgBase64List(boleto.IMGS_BOLETO);
                setNomeCliente(boleto.DS_CONTA);

                const fetchedLinhaDigitavel = boleto.COD_BARRAS;
                const linhas = fetchedLinhaDigitavel
                    .split(";")
                    .map((linha) => linha.trim());
                setLinhaDigitavelList(linhas);
                setSelectedLinhaDigitavel(linhas[0]);
                setLoading(false);
                setShowCpfModal(false);
                console.log("Dados do boleto carregados com sucesso.");
            } catch (err: any) {
                setLoading(false);
                if (err && err.response) {
                    const status = err.response.status;
                    if (status === 400) {
                        AlertError(
                            "Erro de Requisição",
                            "Dados inválidos fornecidos."
                        );
                    } else if (status === 401) {
                        AlertError(
                            "Sessão Expirada",
                            "A sessão expirou ou o token é inválido. Por favor, faça login novamente."
                        );
                    } else if (status === 500) {
                        AlertError(
                            "Erro Interno",
                            "Ocorreu um erro no servidor. Tente novamente mais tarde."
                        );
                    } else {
                        AlertError(
                            "Erro Desconhecido",
                            "Ocorreu um erro inesperado. Tente novamente."
                        );
                    }
                } else {
                    AlertError(
                        "Erro ao Carregar Boleto",
                        "Ocorreu um erro ao carregar o boleto. Por favor, tente novamente."
                    );
                }
                setCpfError(
                    "CPF ou CNPJ inválido ou ocorreu um erro ao validar."
                );
            }
        }
    };

    const handleCopiarLinhaDigitavel = () => {
        navigator.clipboard
            .writeText(selectedLinhaDigitavel)
            .then(() => {
                AlertSuccess("Linha digitável copiada com sucesso!");
            })
            .catch(() => {
                AlertError(
                    "Erro ao copiar a linha digitável.",
                    "Não foi possível copiar a linha digitável."
                );
            });
    };

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    const handleBaixarPdf = () => {
        if (pdfBase64) {
            if (isSafari) {
                const byteCharacters = atob(pdfBase64);
                const byteNumbers = new Uint8Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const blob = new Blob([byteNumbers], {
                    type: "application/pdf",
                });
                const blobUrl = URL.createObjectURL(blob);

                const pdfWindow = window.open();
                if (pdfWindow) {
                    pdfWindow.location.href = blobUrl;
                } else {
                    AlertError(
                        "Não foi possível abrir a janela.",
                        "Certifique-se de que os pop-ups não estão bloqueados no Safari."
                    );
                }
            } else {
                const linkSource = `data:application/pdf;base64,${pdfBase64}`;
                const downloadLink = document.createElement("a");
                const fileName = `Boleto.pdf`;
                downloadLink.href = linkSource;
                downloadLink.download = fileName;
                downloadLink.click();
            }
        } else {
            AlertError(
                "Não foi possível baixar o PDF.",
                "PDF não disponível para download."
            );
        }
    };

    const handleAbrirModal = (imgBase64: string) => {
        setSelectedImg(imgBase64);
        setShowModal(true);
    };

    const handleFecharModal = () => {
        setShowModal(false);
        setSelectedImg("");
    };

    const handleSubmitCpf = (e: React.FormEvent) => {
        e.preventDefault();
        const cpfNumerosLocal = cpf.replace(/\D/g, "");
        console.log(`CPF/CNPJ submetido: ${cpfNumerosLocal}`);

        if (!validarCpfCnpj(cpfNumerosLocal)) {
            setCpfError("CPF ou CNPJ inválido.");
            console.warn("CPF ou CNPJ inválido.");
            return;
        }

        setCpfError("");
        fetchBoletoData(cpfNumerosLocal);
    };

    const handleTentarNovamente = () => {
        setCpfError("");
        setCpf("");
        setShowCpfModal(true);
    };

    const handleChangeCpfCnpj = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        setCpf(inputValue);
    };

    const validarCpfCnpj = (value: string): boolean => {
        const cpfNumerosLocal = value.replace(/\D/g, "");
        if (cpfNumerosLocal.length === 11) {
            return true;
        } else if (cpfNumerosLocal.length === 14) {
            return true;
        }
        return false;
    };

    return (
        <>
            <Modal
                show={showCpfModal && !loading}
                onHide={() => {}}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header>
                    <Modal.Title>
                        Digite seu CPF ou CNPJ para acessar o boleto
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmitCpf}>
                        <Form.Group controlId="formCpfCnpj">
                            <Form.Control
                                value={cpf}
                                onChange={handleChangeCpfCnpj}
                                placeholder="Digite o número do CPF ou CNPJ:"
                                required
                                maxLength={18}
                            />
                            {cpfError && (
                                <Form.Text className="text-danger">
                                    {cpfError}
                                </Form.Text>
                            )}
                        </Form.Group>
                        <Button
                            variant="primary"
                            type="submit"
                            className="mt-3"
                        >
                            Confirmar
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {loading && (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ height: "100vh" }}
                >
                    <Spinner animation="border" variant="primary" role="status">
                        <span className="sr-only">Carregando...</span>
                    </Spinner>
                </div>
            )}

            {!showCpfModal && !loading && (
                <div className="container mt-5 text-center">
                    <h2 className="mb-4">
                        {imgBase64List.length > 1 ? "Boletos" : "Boleto"} de
                        Pagamento
                    </h2>
                    <div className="mb-3">
                        <strong>Cliente:</strong> {nomeCliente}
                    </div>
                    <div className="mb-3 d-flex flex-column flex-sm-row flex-wrap justify-content-center align-items-center">
                        {linhaDigitavelList.length > 1 ? (
                            <>
                                <strong>Linha Digitável:</strong>
                                <div
                                    className="ml-2"
                                    style={{ width: "250px" }}
                                >
                                    <select
                                        value={selectedLinhaDigitavel}
                                        onChange={(e) =>
                                            setSelectedLinhaDigitavel(
                                                e.target.value
                                            )
                                        }
                                        className="form-control"
                                    >
                                        {linhaDigitavelList.map(
                                            (linha, index) => (
                                                <option
                                                    key={index}
                                                    value={linha}
                                                >
                                                    {linha}
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>
                                <br />
                                <Button
                                    variant="outline-secondary"
                                    className="m-2"
                                    onClick={handleCopiarLinhaDigitavel}
                                    title="Copiar Linha Digitável"
                                >
                                    Copiar Código de Barras <FaCopy />
                                </Button>
                            </>
                        ) : (
                            linhaDigitavelList.length === 1 && (
                                <>
                                    <strong>Linha Digitável:</strong>
                                    <div
                                        className="ml-2"
                                        style={{ width: "250px" }}
                                    >
                                        <input
                                            type="text"
                                            value={linhaDigitavelList[0]}
                                            readOnly
                                            className="form-control d-inline-block w-auto"
                                        />
                                    </div>
                                    <br />
                                    <Button
                                        variant="outline-secondary"
                                        className="m-2"
                                        onClick={handleCopiarLinhaDigitavel}
                                        title="Copiar Linha Digitável"
                                    >
                                        Copiar Código de Barras <FaCopy />
                                    </Button>
                                </>
                            )
                        )}
                        <Button
                            variant="outline-primary"
                            className="m-2"
                            onClick={handleBaixarPdf}
                            title="Baixar Boleto em PDF"
                        >
                            Baixar Boleto em PDF <FaDownload />
                        </Button>
                    </div>
                    <div className="border p-3">
                        {imgBase64List.length > 0 ? (
                            <Row className="justify-content-center">
                                {imgBase64List.map((imgBase64, index) => (
                                    <Col
                                        key={index}
                                        xs={12}
                                        sm={12}
                                        md={8}
                                        lg={6}
                                        className="mb-4 d-flex justify-content-center"
                                    >
                                        <Card
                                            className="position-relative"
                                            style={{ width: "100%" }}
                                        >
                                            <Card.Img
                                                variant="top"
                                                src={`data:image/png;base64,${imgBase64}`}
                                                alt={`Boleto ${index + 1}`}
                                                style={{
                                                    cursor: "pointer",
                                                    width: "100%",
                                                    height: "auto",
                                                    objectFit: "contain",
                                                    margin: "0 auto",
                                                }}
                                                onClick={() =>
                                                    handleAbrirModal(imgBase64)
                                                }
                                            />
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        ) : pdfBase64 ? (
                            <object
                                data={`data:application/pdf;base64,${pdfBase64}`}
                                type="application/pdf"
                                width="100%"
                                height="500px"
                            >
                                <p>
                                    Seu navegador não suporta a exibição de
                                    PDFs. Você pode{" "}
                                    <Button
                                        variant="link"
                                        onClick={handleBaixarPdf}
                                    >
                                        baixar o PDF
                                    </Button>
                                    para visualizá-lo.
                                </p>
                            </object>
                        ) : (
                            <p>PDF do boleto não disponível.</p>
                        )}
                    </div>

                    <Modal
                        show={showModal}
                        onHide={handleFecharModal}
                        size="xl"
                        centered
                    >
                        <Modal.Header
                            closeButton
                            className="bg-primary text-align-center"
                        >
                            <Modal.Title>Boleto</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="text-center">
                            {selectedImg && (
                                <img
                                    src={`data:image/png;base64,${selectedImg}`}
                                    alt="Boleto"
                                    className="img-fluid"
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "80vh",
                                    }}
                                />
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleFecharModal}
                            >
                                Fechar
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
            )}
            <Modal
                show={cpfError !== "" && !loading}
                onHide={() => {}}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header>
                    <Modal.Title>Erro de CPF ou CNPJ</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{cpfError}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleTentarNovamente}>
                        Tentar Novamente
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
